import React, { useEffect, useState, useRef, useContext } from 'react';
import { Card, CardContent, IconButton, Stack, Typography, Box, CircularProgress, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Edit as EditIcon } from '@mui/icons-material';

import { translate as t } from 'hooks/useTranslations';
import useStickyState from 'hooks/useStickyState';
import { HeaderConfigurationResponse, PolicyHeaderFieldType, PolicyHeaderIssuingAuthorityType, DraftPolicy, DraftPreface } from 'views/App/Portal/Policy/_types/manuals/draft';
import PropertyList, { PropertyListItem } from 'components/_layouts/PropertyList';
import useManageAttachment from '../../_data_sources/useManageAttachment';
import brokenImage from 'components/ContentEditor/images/image-broken.svg';
import { Attachment } from 'views/App/Portal/Policy/_types/manuals/draft';
import PolicyHeadersEditModal from './PolicyHeadersEditModal';
import { formatDateForLoad, formatDateForSave } from 'views/App/Portal/Policy/_helpers/policyHeaderDateHelper';
import { DraftManualDetailContext } from '../context';
import useManagePolicy from '../../_data_sources/useManagePolicy';
import useManagePreface from '../../_data_sources/useManagePreface';
import { useQueryClient } from 'react-query';

// Type for headers from preface data
type PolicyHeader = {
  type: PolicyHeaderFieldType;
  dateFormat?: string;
  autoSetDate?: boolean;
  agencyLogo?: string;
  issuingAuthorityType?: PolicyHeaderIssuingAuthorityType;
  issuingAuthoritySignature?: string;
  issuingAuthorityName?: string;
  header?: string;
  value?: string;
  state?: 'ENABLED' | 'DISABLED';
};

type PolicyHeadersProps = {
  manualHeaders?: HeaderConfigurationResponse;
  policyHeaders?: PolicyHeader[];
  manualId: string;
  policyData?: DraftPolicy | DraftPreface;
  readOnly?: boolean;
  isPreface?: boolean;
  isLoading?: boolean;
};

const IMAGE_CONTAINER_SX = {
  height: '60px',
  minWidth: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const IMAGE_STYLE = {
  maxHeight: '60px',
  maxWidth: '200px',
  objectFit: 'contain' as const,
};

const getAttachmentKey = (type: 'agencyLogo' | 'signature', id: string): string => {
  return `${type}_${id}`;
};

const PolicyHeaders: React.FC<PolicyHeadersProps> = ({
  manualHeaders,
  policyHeaders,
  manualId,
  policyData,
  readOnly,
  isPreface = false,
  isLoading = false,
}: PolicyHeadersProps) => {
  const { manual } = useContext(DraftManualDetailContext);
  const [expanded, setExpanded] = useStickyState<boolean>('policy-header-display-expanded', false);
  const collapsed = !expanded;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { getAttachment: fetchAttachment } = useManageAttachment();
  const { update: updatePolicy } = useManagePolicy();
  const { update: updatePreface } = useManagePreface();
  const queryClient = useQueryClient();
  const [attachments, setAttachments] = useState<Record<string, Attachment>>({});
  const [loadingAttachments, setLoadingAttachments] = useState<Record<string, boolean>>({});
  const [errorAttachments, setErrorAttachments] = useState<Record<string, boolean>>({});
  const fetchedAttachmentsRef = useRef<Set<string>>(new Set());

  const getHeaderLabel = (key: string): string => {
    return t(`policy.stagedManuals.policyHeaders.${key}`);
  };

  const handleSavePolicyHeaders = async (data: {
    effectiveDate?: string;
    revisedDate?: string;
    agencyLogo?: string;
    issuingAuthoritySignature?: string;
    issuingAuthorityName?: string;
    customFields: Array<{ header: string; value: string; }>;
  }) => {
    if (!policyData) {
      throw new Error('Policy data is required to save headers');
    }

    // Build headerConfiguration array based on manual header order
    const headerConfiguration: any[] = [];

    if (manualHeaders?.order) {
      manualHeaders.order.forEach((orderItem) => {
        const baseConfig = {
          type: orderItem.type,
          state: orderItem.state,
        };

        switch (orderItem.type) {
          case 'EFFECTIVE_DATE':
            if (orderItem.state === 'ENABLED') {
              headerConfiguration.push({
                ...baseConfig,
                dateFormat: manualHeaders.effectiveDateFormat,
                autoSetDate: manualHeaders.autoSetEffectiveDate,
                value: data.effectiveDate || null,
              });
            }
            break;

          case 'REVISED_DATE':
            if (orderItem.state === 'ENABLED') {
              headerConfiguration.push({
                ...baseConfig,
                dateFormat: manualHeaders.revisedDateFormat,
                autoSetDate: manualHeaders.autoSetRevisedDate,
                value: data.revisedDate || null,
              });
            }
            break;

          case 'AGENCY_LOGO':
            if (orderItem.state === 'ENABLED') {
              headerConfiguration.push({
                ...baseConfig,
                agencyLogo: data.agencyLogo || null,
              });
            }
            break;

          case 'ISSUING_AUTHORITY':
            if (orderItem.state === 'ENABLED') {
              headerConfiguration.push({
                ...baseConfig,
                issuingAuthorityType: manualHeaders.issuingAuthorityType,
                issuingAuthoritySignature: data.issuingAuthoritySignature || null,
                issuingAuthorityName: data.issuingAuthorityName || null,
              });
            }
            break;

          case 'CUSTOM1':
          case 'CUSTOM2': {
            const customIndex = orderItem.type === 'CUSTOM1' ? 0 : 1;
            if (orderItem.state === 'ENABLED' && data.customFields[customIndex]) {
              headerConfiguration.push({
                ...baseConfig,
                header: data.customFields[customIndex].header || null,
                value: data.customFields[customIndex].value || null,
              });
            }
            break;
          }
        }
      });
    }

    const updatedData: any = {
      ...policyData,
      headers: headerConfiguration,
    };

    // Use the appropriate update function based on whether it's a preface or policy
    const updateFn = isPreface ? updatePreface : updatePolicy;
    const queryKey = isPreface ? 'draft_preface' : 'draft_policy';

    // Save with the new header configuration
    await updateFn(
      manualId,
      policyData.id,
      updatedData,
      () => {
        // Success callback - invalidate queries to refetch data
        queryClient.invalidateQueries([queryKey, manualId, policyData.id]);
        queryClient.invalidateQueries(['draft_manual', manualId]);
      },
      (error) => {
        // Error callback - rethrow to be caught by modal
        throw error;
      }
    );
  };

  const renderAttachmentImage = (
    attachmentKey: string,
    imageUrl: string,
    altText: string
  ): React.ReactNode => {
    const isLoading = loadingAttachments[attachmentKey];
    const hasError = errorAttachments[attachmentKey];

    return (
      <Box sx={IMAGE_CONTAINER_SX}>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : hasError ? (
          <img
            src={brokenImage}
            alt="Failed to load"
            style={{
              height: '60px',
              width: '100%',
              opacity: 0.2,
            }}
          />
        ) : (
          <img
            src={imageUrl}
            alt={altText}
            style={IMAGE_STYLE}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              setErrorAttachments((prev) => ({ ...prev, [attachmentKey]: true }));
            }}
          />
        )}
      </Box>
    );
  };

  const renderIssuingAuthority = (
    issuingAuthorityType: PolicyHeaderIssuingAuthorityType | undefined,
    signatureId: string | undefined,
    authorityName: string | undefined
  ): React.ReactNode => {
    const authType = issuingAuthorityType ?? 'SIGNATURE_AND_NAME';

    if (authType === 'SIGNATURE_AND_NAME' || authType === 'SIGNATURE_ONLY') {
      if (signatureId) {
        const signatureKey = getAttachmentKey('signature', signatureId);
        const signatureAttachment = attachments[signatureKey];
        const signatureUrl = signatureAttachment?.url || signatureId;

        return (
          <Stack direction="column" spacing={1} alignItems="flex-start">
            {renderAttachmentImage(signatureKey, signatureUrl, 'Signature')}
            {authType === 'SIGNATURE_AND_NAME' && authorityName && (
              <Typography>{authorityName}</Typography>
            )}
          </Stack>
        );
      } else if (authType === 'SIGNATURE_AND_NAME' && authorityName) {
        return authorityName;
      }
    } else if (authType === 'NAME_ONLY' && authorityName) {
      return authorityName;
    }

    return null;
  };

  const renderAgencyLogo = (logoId: string): PropertyListItem => {
    const attachmentKey = getAttachmentKey('agencyLogo', logoId);
    const attachment = attachments[attachmentKey];
    const imageUrl = attachment?.url || logoId;

    return {
      label: getHeaderLabel('agencyLogo'),
      value: renderAttachmentImage(attachmentKey, imageUrl, 'Agency Logo'),
    };
  };

  const renderDateValue = (value?: string, dateFormat?: string, autoSet?: boolean): string => {
    if (value) {
      const parsed = formatDateForLoad(value, dateFormat);
      return formatDateForSave(parsed, dateFormat) ?? value;
    } else if (autoSet === true) {
      return '(autofill)';
    } else {
      return '--';
    }
  };

  // Fetch attachments when manualHeaders or headers change
  useEffect(() => {
    if (!manualId) return;

    const attachmentIdsToFetch: Array<{ id: string; key: string; }> = [];

    // Helper to queue attachment for fetching if not already attempted
    const queueAttachment = (attachmentId: string | undefined, keyPrefix: 'agencyLogo' | 'signature') => {
      if (!attachmentId) return;

      const key = getAttachmentKey(keyPrefix, attachmentId);
      if (!fetchedAttachmentsRef.current.has(key)) {
        attachmentIdsToFetch.push({ id: attachmentId, key });
        fetchedAttachmentsRef.current.add(key);
      }
    };

    // Check manualHeaders first
    if (manualHeaders && manualHeaders.state === 'ENABLED') {
      if (manualHeaders.agencyLogo) {
        queueAttachment(manualHeaders.agencyLogo, 'agencyLogo');
      }
      if (manualHeaders.issuingAuthoritySignature) {
        queueAttachment(manualHeaders.issuingAuthoritySignature, 'signature');
      }
    }

    // Check policyHeaders
    if (policyHeaders) {
      policyHeaders.forEach((header) => {
        if (header.state === 'DISABLED') return;
        if (header.type === 'AGENCY_LOGO' && header.agencyLogo) {
          queueAttachment(header.agencyLogo, 'agencyLogo');
        } else if (header.type === 'ISSUING_AUTHORITY' && header.issuingAuthoritySignature) {
          queueAttachment(header.issuingAuthoritySignature, 'signature');
        }
      });
    }

    // Fetch all attachments that need to be fetched
    attachmentIdsToFetch.forEach(({ id, key }) => {
      setLoadingAttachments((prev) => ({ ...prev, [key]: true }));

      fetchAttachment({ manualId, attachmentId: id })
        .then((response) => {
          setAttachments((prev) => ({ ...prev, [key]: response as any }));
          setLoadingAttachments((prev) => ({ ...prev, [key]: false }));
        })
        .catch((error) => {
          console.error(`Failed to fetch attachment ${id}:`, error);
          setErrorAttachments((prev) => ({ ...prev, [key]: true }));
          setLoadingAttachments((prev) => ({ ...prev, [key]: false }));
        });
    });
  }, [manualHeaders, policyHeaders, manualId]);

  let headerItems: PropertyListItem[] = [];

  // Build a lookup map for policy-specific header values
  const policyHeaderMap = new Map<string, any>();
  if (policyHeaders && policyHeaders.length > 0) {
    policyHeaders.forEach((header) => {
      policyHeaderMap.set(header.type, header);
    });
  }

  // Always use manualHeaders.order as the source of truth for which headers to display
  // Override with policy-specific values when available
  if (manualHeaders && manualHeaders.state === 'ENABLED' && manualHeaders.order) {
    manualHeaders.order.forEach((orderItem) => {
      if (orderItem.state === 'DISABLED') {
        return;
      }

      // Check if there's a policy-specific override for this header type
      const policyHeader = policyHeaderMap.get(orderItem.type);

      switch (orderItem.type) {
        case 'EFFECTIVE_DATE': {
          const dateFormat = manualHeaders.effectiveDateFormat;
          const autoSetDate = manualHeaders.autoSetEffectiveDate;
          const value = autoSetDate ? undefined : policyHeader?.value;

          headerItems.push({
            label: getHeaderLabel('effectiveDate'),
            value: renderDateValue(value, dateFormat, autoSetDate),
          });
          break;
        }

        case 'REVISED_DATE': {
          const dateFormat = manualHeaders.revisedDateFormat;
          const autoSetDate = manualHeaders.autoSetRevisedDate;
          const value = autoSetDate ? undefined : policyHeader?.value;

          headerItems.push({
            label: getHeaderLabel('revisedDate'),
            value: renderDateValue(value, dateFormat, autoSetDate),
          });
          break;
        }

        case 'AGENCY_LOGO': {
          const logoId = policyHeader ? policyHeader.agencyLogo : manualHeaders.agencyLogo;
          if (logoId) {
            headerItems.push(renderAgencyLogo(logoId));
          }
          break;
        }

        case 'ISSUING_AUTHORITY': {
          const authType = manualHeaders.issuingAuthorityType;
          const signatureId = authType !== 'NAME_ONLY'
            ? (policyHeader ? policyHeader.issuingAuthoritySignature : manualHeaders.issuingAuthoritySignature)
            : undefined;
          const authorityName = policyHeader ? policyHeader.issuingAuthorityName : manualHeaders.issuingAuthorityName;

          const issuingAuthValue = renderIssuingAuthority(authType, signatureId, authorityName);

          if (issuingAuthValue) {
            headerItems.push({
              label: getHeaderLabel('issuingAuthority'),
              value: issuingAuthValue,
            });
          }
          break;
        }

        case 'CUSTOM1':
        case 'CUSTOM2': {
          const customIndex = orderItem.type === 'CUSTOM1' ? 0 : 1;

          // Use policy-specific custom field if available, otherwise fall back to manual
          let customHeader: string | undefined;
          let customValue: string | undefined;

          if (policyHeader) {
            customHeader = policyHeader.header;
            customValue = policyHeader.value;
          } else if (manualHeaders.customFields) {
            const customField = manualHeaders.customFields[customIndex];
            customHeader = customField?.header;
            customValue = customField?.value;
          }

          if (customHeader && customValue) {
            headerItems.push({
              label: customHeader,
              value: customValue,
            });
          }
          break;
        }
      }
    });
  }

  // Show loading spinner if data is still loading
  if (isLoading) {
    return (
      <Card>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  return headerItems.length > 0 ? (
    <Card>
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          py: 3,
          cursor: 'pointer',
          borderBottom: !collapsed ? '1px solid' : 'none',
          borderBottomColor: 'divider',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Typography variant="h6">{t('policy.stagedManuals.policyHeaders.title')}</Typography>
        <IconButton size="small">
          {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>
      <Collapse in={!collapsed}>
        <CardContent sx={{ py: 6, px: { xs: 4, sm: 10 }, position: 'relative' }}>
          {!readOnly && (
            <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsModalOpen(true);
                }}
                sx={{
                  width: 32,
                  height: 32,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                aria-label="Edit policy headers"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <PropertyList columns={3} items={headerItems} />
        </CardContent>
      </Collapse>
      <PolicyHeadersEditModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        manualHeaders={manualHeaders}
        policyHeaders={policyHeaders}
        manualId={manualId}
        policyData={policyData}
        attachments={manual?.attachments || []}
        manualLevel={manual?.manualLevel}
        onSave={handleSavePolicyHeaders}
      />
    </Card>
  ) : null;
};

export default PolicyHeaders;